"use client";

import React, {
  createContext,
  useContext,
  useCallback,
  useRef,
  ReactNode,
} from "react";
import { toast as sonnerToast } from "sonner";
import { logger } from "@/utils/logger";

interface Toast {
  id: string;
  message: string;
  type: "success" | "error" | "warning" | "info";
  duration?: number;
  persistent?: boolean;
}

interface ToastContextValue {
  toasts: Toast[];
  showToast: (
    message: string,
    type?: Toast["type"],
    options?: Partial<Pick<Toast, "duration" | "persistent">>
  ) => void;
  removeToast: (id: string) => void;
  clearAll: () => void;
}

const ToastContext = createContext<ToastContextValue | undefined>(undefined);

// Time window for deduplication (in milliseconds)
const DEDUP_TIME_WINDOW = 3000; // 3 seconds

interface RecentToastEntry {
  message: string;
  type: Toast["type"];
  timestamp: number;
}

export function ToastProvider({ children }: { children: ReactNode }) {
  // Track recent toasts for deduplication
  const recentToastsRef = useRef<RecentToastEntry[]>([]);

  const removeToast = useCallback((id: string) => {
    sonnerToast.dismiss(id);
  }, []);

  const clearAll = useCallback(() => {
    sonnerToast.dismiss();
    recentToastsRef.current = [];
  }, []);

  // Clean up old entries from recent toasts tracker
  const cleanupOldEntries = useCallback(() => {
    const now = Date.now();
    recentToastsRef.current = recentToastsRef.current.filter(
      entry => now - entry.timestamp < DEDUP_TIME_WINDOW
    );
  }, []);

  const showToast = useCallback(
    (
      message: string,
      type: Toast["type"] = "info",
      options: Partial<Pick<Toast, "duration" | "persistent">> = {}
    ) => {
      cleanupOldEntries();

      const now = Date.now();
      const isDuplicate = recentToastsRef.current.some(
        entry =>
          entry.message === message &&
          entry.type === type &&
          now - entry.timestamp < DEDUP_TIME_WINDOW
      );

      if (isDuplicate) {
        logger.debug(`Toast deduplicated: ${type} - ${message}`, {
          component: "ToastProvider",
        });
        return;
      }

      recentToastsRef.current.push({
        message,
        type,
        timestamp: now,
      });

      const duration = options.duration ?? (type === "error" ? 8000 : 5000);
      const toastOptions = options.persistent
        ? { duration: Infinity }
        : { duration };

      switch (type) {
        case "success":
          sonnerToast.success(message, toastOptions);
          break;
        case "error":
          sonnerToast.error(message, toastOptions);
          break;
        case "warning":
          sonnerToast.warning(message, toastOptions);
          break;
        case "info":
        default:
          sonnerToast.info(message, toastOptions);
          break;
      }

      logger.debug(`Toast shown: ${type} - ${message}`, {
        component: "ToastProvider",
      });
    },
    [cleanupOldEntries]
  );

  const value: ToastContextValue = {
    toasts: [], // Sonner manages display; empty for backward compat
    showToast,
    removeToast,
    clearAll,
  };

  return (
    <ToastContext.Provider value={value}>{children}</ToastContext.Provider>
  );
}

export function useToast() {
  const context = useContext(ToastContext);
  if (!context) {
    throw new Error("useToast must be used within a ToastProvider");
  }
  return context;
}
